import * as React from 'react'
import { useParams } from 'react-router-dom'
import { usePlan } from '@/features/plans/api/get-plan'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import ExternalPlanDetails from './ExternalPlanDetails'

export default function PlanDetails() {
    const { id = '' } = useParams()
    const { data, isLoading, error } = usePlan({ planId: id })

    if (isLoading) return <div>Loading plan…</div>
    if (error || !data) return <div className='text-destructive'>Failed to load plan.</div>

    const isAddon = (data as any).isAddon ?? false
    const features = (data as any).features ?? []

    return (
        <div className='grid grid-cols-2 gap-4'>
            <Card>
                <CardHeader>
                    <CardTitle className='flex items-center gap-3'>
                        <span>{data.name}</span>
                        {isAddon ? <Badge variant='secondary'>Add-on</Badge> : <Badge variant='outline'>Plan</Badge>}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <div>
                            <div className='text-sm text-muted-foreground'>Plan ID</div>
                            <div className='font-medium'>{(data as any).planId}</div>
                        </div>
                        <div>
                            <div className='text-sm text-muted-foreground'>SKU</div>
                            <div className='font-medium'>{(data as any).planSku}</div>
                        </div>
                        <div>
                            <div className='text-sm text-muted-foreground'>Billing Interval</div>
                            <div className='font-medium capitalize'>{(data as any).billingInterval}</div>
                        </div>
                        <div>
                            <div className='text-sm text-muted-foreground'>Created</div>
                            <div className='font-medium'>{new Date((data as any).createdAt).toLocaleString()}</div>
                        </div>
                        <div>
                            <div className='text-sm text-muted-foreground'>Updated</div>
                            <div className='font-medium'>{new Date((data as any).updatedAt).toLocaleString()}</div>
                        </div>
                    </div>
                    <div className='mt-4'>
                        <div className='text-sm text-muted-foreground'>Description</div>
                        <div>{data.description || '—'}</div>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle>Features</CardTitle>
                </CardHeader>
                <CardContent>
                    {features.length === 0 ? (
                        <div className='text-muted-foreground'>No features.</div>
                    ) : (
                        <div className='divide-y rounded-md border'>
                            {features.map((pf: any, idx: number) => {
                                const featureKey = pf?.featureKey || pf?.featureId
                                const typeBadge = pf?.limitType ? <Badge variant='outline' className='capitalize'>{pf.limitType} Limit</Badge> : null
                                const scopeBadge = pf?.pooledScope ? <Badge variant='secondary' className='capitalize'>{pf.pooledScope} Scope</Badge> : null
                                const displayLimit = pf?.limitInt ?? pf?.limitDecimal ?? '—'
                                return (
                                    <div key={idx} className='flex items-center justify-between gap-3 p-3'>
                                        <div className='min-w-0'>
                                            <div className='font-medium truncate'>{featureKey}</div>
                                            <div className='mt-1 flex flex-wrap items-center gap-2'>
                                                {typeBadge}
                                                {scopeBadge}
                                            </div>
                                        </div>
                                        <div className='text-right'>
                                            <div className='text-sm text-muted-foreground'>Limit</div>
                                            <div className='font-medium'>{displayLimit}</div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle>Subscribers</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className='text-muted-foreground'>No subscribers.</div>
                </CardContent>
            </Card>
            <ExternalPlanDetails plan={data} />
        </div>
    )
}


